import { useState } from "react";
import { db, auth } from "./firebase";
import { collection, addDoc } from "firebase/firestore";
import { signOut } from "firebase/auth";
import "./App.css";

const RELATION_OPTIONS = [
  "Spouse","Father","Mother","Son","Daughter","Brother","Sister","Guardian","Other",
];

const DEPOSITORY_OPTIONS = ["NSDL", "CDSL"];
const CATEGORY_OPTIONS   = ["Individual", "HUF", "Minor", "Other"];
const ACCOUNT_TYPE_OPTIONS = ["Savings", "Current", "NRE", "NRO", "Other"];

const emptyBank = () => ({
  bankName: "",
  branch: "",
  nameAsPerBank: "",
  accNumber: "",
  accountType: "",
  ifsc: "",
  micr: "",
  customerId: "",
  netBankingId: "",
  mo: "",
  email: "",       
  atmCardNo: "",   
  expiry: "",      
  cvv: "",       
  note: "",       
});

export default function Form({ user, setUser, embedded = false }) {
  const [form, setForm] = useState({
    // ── DP Details ──
    dpName: "",
    depository: "",
    category: "",
    representativeAssessee: "",
    dpId: "",
    // boClientId removed — now combined into dpId field
    pan: "",
    dob: "",
    nameAsPerPan: "",
    mobile: "",
    email: "",
    jointWith: "",
    upiId: "",
    bank1: "",
    bank2: "",        
    primaryBank: "",
    aadhaar: "",
    dpNote: "",     
    // ── Basic / legacy ──
    fullName: "",
    code: "",
    // ── Other Details (all optional) ──
    reference: "",
    sourceForIpo: "",
    note: "",
  });

  const [bankEntries, setBankEntries] = useState([emptyBank()]);

  // Nominee is fully optional 
  const [nominee, setNominee] = useState({
    nameAsPerPan: "",
    aadhaarNumber: "",
    relation: "",
  });

  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // ── Field helpers ──────────────────────────────────────────────
  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const setNomineeField = (key, val) => setNominee((n) => ({ ...n, [key]: val }));
  const setBankField = (idx, key, val) =>
    setBankEntries((entries) => entries.map((e, i) => (i === idx ? { ...e, [key]: val } : e)));

  const addBank    = () => setBankEntries((e) => [...e, emptyBank()]);
  const removeBank = (idx) => setBankEntries((e) => e.filter((_, i) => i !== idx));

  const validate = () => {
    const e = {};

    // ── DP Details — all required  ──
    if (!form.fullName.trim())               e.fullName               = "Required";
    if (!form.code.trim())                   e.code                   = "Required";
    if (!form.dpName.trim())                 e.dpName                 = "Required";
    if (!form.depository)                    e.depository             = "Required";
    if (!form.category)                      e.category               = "Required";
    if (!form.representativeAssessee.trim()) e.representativeAssessee = "Required";
    if (!form.dpId.trim())                   e.dpId                   = "Required";
    if (!form.dob)                           e.dob                    = "Required";
    if (!form.nameAsPerPan.trim())           e.nameAsPerPan           = "Required";
    if (!form.jointWith.trim())              e.jointWith              = "Required";
    if (!form.bank1.trim())                  e.bank1                  = "Required";
    if (!form.primaryBank.trim())            e.primaryBank            = "Required";

    // PAN — required + format
    if (!form.pan.trim()) {
      e.pan = "Required";
    } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.pan)) {
      e.pan = "Invalid PAN format (ABCDE1234F)";
    }

    // Email — required + format
    if (!form.email.trim())
      e.email = "Required";
    else if (!/\S+@\S+\.\S+/.test(form.email))
      e.email = "Enter a valid email";

    // Mobile — required + format
    if (!form.mobile.trim())
      e.mobile = "Required";
    else if (!/^\d{10}$/.test(form.mobile))
      e.mobile = "Enter a valid 10-digit number";

    // Aadhaar — required + format
    if (!form.aadhaar.trim())
      e.aadhaar = "Required";
    else if (!/^\d{12}$/.test(form.aadhaar))
      e.aadhaar = "Aadhaar must be 12 digits";

    // UPI — required + format
    if (!form.upiId.trim())
      e.upiId = "Required";
    else if (!form.upiId.includes("@"))
      e.upiId = "Invalid UPI ID (must contain @)";

    bankEntries.forEach((b, i) => {
      if (!b.bankName.trim())      e[`bank_${i}_bankName`]      = "Required";
      if (!b.branch.trim())        e[`bank_${i}_branch`]        = "Required";
      if (!b.nameAsPerBank.trim()) e[`bank_${i}_nameAsPerBank`] = "Required";
      if (!b.accNumber.trim())     e[`bank_${i}_accNumber`]     = "Required";
      if (!b.accountType)          e[`bank_${i}_accountType`]   = "Required";
      if (!b.customerId.trim())    e[`bank_${i}_customerId`]    = "Required";
      if (!b.netBankingId.trim())  e[`bank_${i}_netBankingId`]  = "Required";
      if (!b.mo.trim())            e[`bank_${i}_mo`]            = "Required";

      // IFSC — required + format
      if (!b.ifsc.trim()) {
        e[`bank_${i}_ifsc`] = "Required";
      } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(b.ifsc)) {
        e[`bank_${i}_ifsc`] = "Invalid IFSC format";
      }

      // MICR — required + format
      if (!b.micr.trim()) {
        e[`bank_${i}_micr`] = "Required";
      } else if (!/^\d{9}$/.test(b.micr)) {
        e[`bank_${i}_micr`] = "MICR must be exactly 9 digits";
      }
    });

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Submit ─────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) {
      const firstError = document.querySelector(".input.error, .select.error");
      if (firstError) firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setLoading(true);
    try {
      const ref = collection(db, "admins", user.uid, "users");
      await addDoc(ref, {
        ...form,
        pan: form.pan.toUpperCase(),
        bankDetails: bankEntries,
        nomineeDetails: nominee,
        createdAt: new Date(),
      });
      setSuccess(true);
      setForm({
        dpName:"",depository:"",category:"",representativeAssessee:"",
        dpId:"",pan:"",dob:"",nameAsPerPan:"",mobile:"",email:"",
        jointWith:"",upiId:"",bank1:"",bank2:"",primaryBank:"",aadhaar:"",dpNote:"",
        fullName:"",code:"",reference:"",sourceForIpo:"",note:"",
      });
      setBankEntries([emptyBank()]);
      setNominee({ nameAsPerPan:"", aadhaarNumber:"", relation:"" });
      setErrors({});
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      alert("Error saving data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => { await signOut(auth); setUser(null); };

  const ic = (key) => `input${errors[key] ? " error" : ""}`;
  const sc = (key) => `select${errors[key] ? " error" : ""}`;

  return (
    <div className="app-wrapper">
      <div className="card">
        {/* Header */}
        <div className="page-header">
          <div className="page-header-icon">📋</div>
          <div style={{ flex: 1 }}>
            <h1>Client Form</h1>
            <p>Logged in as {user.email}</p>
          </div>
        </div>

        {success && (
          <div style={{background:"#f0fdf4",borderBottom:"1px solid #bbf7d0",padding:"14px 32px",
            color:"#166534",fontSize:14,fontWeight:600,display:"flex",alignItems:"center",gap:8}}>
            ✅ Client data saved successfully!
          </div>
        )}

        <div className="form-body">

          {/* ══════════════════════════════════════════════
              SECTION 1 — DP DETAILS
          ══════════════════════════════════════════════ */}
          <div className="form-section">
            <div className="section-header">
              <div className="section-icon">🗂️</div>
              <span className="section-title">DP Details</span>
            </div>

            <div className="grid-2">

              {/* Full Name */}
              <div className="field">
                <label className="field-label">Full Name <span className="required">*</span></label>
                <input className={ic("fullName")} placeholder="Full name" value={form.fullName}
                  onChange={(e) => setField("fullName", e.target.value)} />
                {errors.fullName && <span className="error-msg">⚠ {errors.fullName}</span>}
              </div>

              {/* Code */}
              <div className="field">
                <label className="field-label">Code <span className="required">*</span></label>
                <input className={ic("code")} placeholder="Client Code" value={form.code}
                  onChange={(e) => setField("code", e.target.value)} />
                {errors.code && <span className="error-msg">⚠ {errors.code}</span>}
              </div>

              {/* DP Name */}
              <div className="field">
                <label className="field-label">DP Name <span className="required">*</span></label>
                <input className={ic("dpName")} placeholder="Depository Participant Name" value={form.dpName}
                  onChange={(e) => setField("dpName", e.target.value)} />
                {errors.dpName && <span className="error-msg">⚠ {errors.dpName}</span>}
              </div>

              {/* Depository */}
              <div className="field">
                <label className="field-label">Depository <span className="required">*</span></label>
                <select className={sc("depository")} value={form.depository}
                  onChange={(e) => setField("depository", e.target.value)}>
                  <option value="">-- Select --</option>
                  {DEPOSITORY_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                {errors.depository && <span className="error-msg">⚠ {errors.depository}</span>}
              </div>

              {/* Category */}
              <div className="field">
                <label className="field-label">Category <span className="required">*</span></label>
                <select className={sc("category")} value={form.category}
                  onChange={(e) => setField("category", e.target.value)}>
                  <option value="">-- Select --</option>
                  {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {errors.category && <span className="error-msg">⚠ {errors.category}</span>}
              </div>

              {/* Representative Assessee */}
              <div className="field">
                <label className="field-label">Representative Assessee <span className="required">*</span></label>
                <input className={ic("representativeAssessee")} placeholder="Representative Assessee"
                  value={form.representativeAssessee}
                  onChange={(e) => setField("representativeAssessee", e.target.value)} />
                {errors.representativeAssessee && <span className="error-msg">⚠ {errors.representativeAssessee}</span>}
              </div>

              {/* DP ID + BO Client ID — combined into one field, full width */}
              <div className="field" style={{ gridColumn: "1 / -1" }}>
                <label className="field-label">DP ID + BO Client ID <span className="required">*</span></label>
                <input className={`${ic("dpId")} mono`} placeholder="e.g. 1206140001347087" value={form.dpId}
                  onChange={(e) => setField("dpId", e.target.value)} />
                <span className="field-hint">Enter combined DP ID and BO/Client ID (e.g. 1206140001347087 → DP: 12061400, BO: 01347087)</span>
                {errors.dpId && <span className="error-msg">⚠ {errors.dpId}</span>}
              </div>

              {/* PAN */}
              <div className="field">
                <label className="field-label">PAN Number <span className="required">*</span></label>
                <input className={`${ic("pan")} mono`} placeholder="ABCDE1234F" maxLength={10}
                  value={form.pan} onChange={(e) => setField("pan", e.target.value.toUpperCase())} />
                <span className="field-hint">{form.pan.length}/10 characters</span>
                {errors.pan && <span className="error-msg">⚠ {errors.pan}</span>}
              </div>

              {/* DOB / DOI */}
              <div className="field">
                <label className="field-label">DOB / DOI <span className="required">*</span></label>
                <input className={ic("dob")} type="date" value={form.dob}
                  onChange={(e) => setField("dob", e.target.value)} />
                {errors.dob && <span className="error-msg">⚠ {errors.dob}</span>}
              </div>

              {/* Name as per PAN */}
              <div className="field">
                <label className="field-label">Name as per PAN <span className="required">*</span></label>
                <input className={ic("nameAsPerPan")} placeholder="Name as it appears on PAN" value={form.nameAsPerPan}
                  onChange={(e) => setField("nameAsPerPan", e.target.value)} />
                {errors.nameAsPerPan && <span className="error-msg">⚠ {errors.nameAsPerPan}</span>}
              </div>

              {/* Mobile */}
              <div className="field">
                <label className="field-label">Mobile (MO) <span className="required">*</span></label>
                <input className={ic("mobile")} type="tel" placeholder="10-digit mobile" maxLength={10}
                  value={form.mobile} onChange={(e) => setField("mobile", e.target.value.replace(/\D/g,""))} />
                {errors.mobile && <span className="error-msg">⚠ {errors.mobile}</span>}
              </div>

              {/* Email */}
              <div className="field">
                <label className="field-label">Email Address <span className="required">*</span></label>
                <input className={ic("email")} type="email" placeholder="client@example.com" value={form.email}
                  onChange={(e) => setField("email", e.target.value)} />
                {errors.email && <span className="error-msg">⚠ {errors.email}</span>}
              </div>

              {/* Joint With */}
              <div className="field">
                <label className="field-label">Joint With <span className="required">*</span></label>
                <input className={ic("jointWith")} placeholder="Joint account holder name" value={form.jointWith}
                  onChange={(e) => setField("jointWith", e.target.value)} />
                {errors.jointWith && <span className="error-msg">⚠ {errors.jointWith}</span>}
              </div>

              {/* UPI ID — required  */}
              <div className="field">
                <label className="field-label">UPI ID <span className="required">*</span></label>
                <input className={ic("upiId")} placeholder="example@upi" value={form.upiId}
                  onChange={(e) => setField("upiId", e.target.value)} />
                {errors.upiId && <span className="error-msg">⚠ {errors.upiId}</span>}
              </div>

              {/* Bank 1 — required */}
              <div className="field">
                <label className="field-label">Bank 1 <span className="required">*</span></label>
                <input className={ic("bank1")} placeholder="Primary bank name (short)" value={form.bank1}
                  onChange={(e) => setField("bank1", e.target.value)} />
                <span className="field-hint">Brief entry — full details in Bank Details section below</span>
                {errors.bank1 && <span className="error-msg">⚠ {errors.bank1}</span>}
              </div>

              {/* Bank 2 — OPTIONAL (green in image) */}
              <div className="field">
                <label className="field-label" style={{color:"var(--success,#16a34a)"}}>Bank 2 <span style={{fontSize:12,fontWeight:400}}>(Optional)</span></label>
                <input className="input" placeholder="Secondary bank name (short)" value={form.bank2}
                  onChange={(e) => setField("bank2", e.target.value)} />
              </div>

              {/* Primary Bank — required  */}
              <div className="field">
                <label className="field-label">Primary Bank <span className="required">*</span></label>
                <input className={ic("primaryBank")} placeholder="Primary bank identifier" value={form.primaryBank}
                  onChange={(e) => setField("primaryBank", e.target.value)} />
                {errors.primaryBank && <span className="error-msg">⚠ {errors.primaryBank}</span>}
              </div>

              {/* Aadhaar */}
              <div className="field">
                <label className="field-label">Aadhaar Number <span className="required">*</span></label>
                <input className={`${ic("aadhaar")} mono`} placeholder="12-digit Aadhaar" maxLength={12}
                  value={form.aadhaar} onChange={(e) => setField("aadhaar", e.target.value.replace(/\D/g,""))} />
                <span className="field-hint">{form.aadhaar.length}/12 digits</span>
                {errors.aadhaar && <span className="error-msg">⚠ {errors.aadhaar}</span>}
              </div>

              {/* Note (DP) — OPTIONAL  */}
              <div className="field" style={{ gridColumn: "1 / -1" }}>
                <label className="field-label" style={{color:"var(--success,#16a34a)"}}>Note (DP) <span style={{fontSize:12,fontWeight:400}}>(Optional)</span></label>
                <textarea className="input" rows={2} placeholder="Any note for this DP record"
                  value={form.dpNote} onChange={(e) => setField("dpNote", e.target.value)} />
              </div>

            </div>
          </div>

          {/* ══════════════════════════════════════════════
              SECTION 2 — NOMINEE DETAILS (fully optional )
          ══════════════════════════════════════════════ */}
          <div className="form-section">
            <div className="section-header">
              <div className="section-icon">👥</div>
              <span className="section-title" style={{color:"var(--success,#16a34a)"}}>Nominee Details</span>
            </div>
            <div className="grid-3">
              <div className="field">
                <label className="field-label">Name as per PAN</label>
                <input className="input" placeholder="Nominee's full name"
                  value={nominee.nameAsPerPan} onChange={(e) => setNomineeField("nameAsPerPan", e.target.value)} />
              </div>
              <div className="field">
                <label className="field-label">Aadhaar Number</label>
                <input className="input mono" placeholder="12-digit Aadhaar" maxLength={12}
                  value={nominee.aadhaarNumber}
                  onChange={(e) => setNomineeField("aadhaarNumber", e.target.value.replace(/\D/g,""))} />
                <span className="field-hint">{nominee.aadhaarNumber.length}/12 digits</span>
              </div>
              <div className="field">
                <label className="field-label">Relation</label>
                <select className="select" value={nominee.relation}
                  onChange={(e) => setNomineeField("relation", e.target.value)}>
                  <option value="">-- Select Relation --</option>
                  {RELATION_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* ══════════════════════════════════════════════
              SECTION 3 — BANK DETAILS (multiple)
          ══════════════════════════════════════════════ */}
          <div className="form-section">
            <div className="section-header">
              <div className="section-icon">🏦</div>
              <span className="section-title">Bank Details</span>
              <span style={{marginLeft:8,fontSize:12,color:"var(--text-muted)",fontWeight:500}}>(Multiple banks allowed)</span>
            </div>

            {bankEntries.map((bank, idx) => (
              <div className="bank-entry" key={idx}>
                <div className="bank-entry-header">
                  <span className="bank-badge">Account #{idx + 1}</span>
                  {bankEntries.length > 1 && (
                    <button className="btn btn-remove" onClick={() => removeBank(idx)}>✕ Remove</button>
                  )}
                </div>
                <div className="grid-2">

                  {/* Bank Name — required */}
                  <div className="field">
                    <label className="field-label">Bank Name <span className="required">*</span></label>
                    <input className={`input${errors[`bank_${idx}_bankName`] ? " error" : ""}`}
                      placeholder="Bank name" value={bank.bankName}
                      onChange={(e) => setBankField(idx, "bankName", e.target.value)} />
                    {errors[`bank_${idx}_bankName`] && <span className="error-msg">⚠ {errors[`bank_${idx}_bankName`]}</span>}
                  </div>

                  {/* Branch — required */}
                  <div className="field">
                    <label className="field-label">Branch <span className="required">*</span></label>
                    <input className={`input${errors[`bank_${idx}_branch`] ? " error" : ""}`}
                      placeholder="Branch name / city" value={bank.branch}
                      onChange={(e) => setBankField(idx, "branch", e.target.value)} />
                    {errors[`bank_${idx}_branch`] && <span className="error-msg">⚠ {errors[`bank_${idx}_branch`]}</span>}
                  </div>

                  {/* Name as per Bank — required */}
                  <div className="field">
                    <label className="field-label">Name as per Bank <span className="required">*</span></label>
                    <input className={`input${errors[`bank_${idx}_nameAsPerBank`] ? " error" : ""}`}
                      placeholder="Account holder name" value={bank.nameAsPerBank}
                      onChange={(e) => setBankField(idx, "nameAsPerBank", e.target.value)} />
                    {errors[`bank_${idx}_nameAsPerBank`] && <span className="error-msg">⚠ {errors[`bank_${idx}_nameAsPerBank`]}</span>}
                  </div>

                  {/* Account Number — required */}
                  <div className="field">
                    <label className="field-label">Account Number <span className="required">*</span></label>
                    <input className={`input mono${errors[`bank_${idx}_accNumber`] ? " error" : ""}`}
                      placeholder="Account number" value={bank.accNumber}
                      onChange={(e) => setBankField(idx, "accNumber", e.target.value.replace(/\D/g,""))} />
                    {errors[`bank_${idx}_accNumber`] && <span className="error-msg">⚠ {errors[`bank_${idx}_accNumber`]}</span>}
                  </div>

                  {/* Account Type — required */}
                  <div className="field">
                    <label className="field-label">Account Type <span className="required">*</span></label>
                    <select className={`select${errors[`bank_${idx}_accountType`] ? " error" : ""}`}
                      value={bank.accountType}
                      onChange={(e) => setBankField(idx, "accountType", e.target.value)}>
                      <option value="">-- Select Type --</option>
                      {ACCOUNT_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    {errors[`bank_${idx}_accountType`] && <span className="error-msg">⚠ {errors[`bank_${idx}_accountType`]}</span>}
                  </div>

                  {/* IFSC — required + format */}
                  <div className="field">
                    <label className="field-label">IFSC Code <span className="required">*</span></label>
                    <input className={`input mono${errors[`bank_${idx}_ifsc`] ? " error" : ""}`}
                      placeholder="SBIN0001234" maxLength={11} value={bank.ifsc}
                      onChange={(e) => setBankField(idx, "ifsc", e.target.value.toUpperCase())} />
                    <span className="field-hint">{bank.ifsc.length}/11 characters</span>
                    {errors[`bank_${idx}_ifsc`] && <span className="error-msg">⚠ {errors[`bank_${idx}_ifsc`]}</span>}
                  </div>

                  {/* MICR — required + format */}
                  <div className="field">
                    <label className="field-label">MICR Code <span className="required">*</span></label>
                    <input className={`input mono${errors[`bank_${idx}_micr`] ? " error" : ""}`}
                      placeholder="9-digit MICR" maxLength={9} value={bank.micr}
                      onChange={(e) => setBankField(idx, "micr", e.target.value.replace(/\D/g,""))} />
                    <span className="field-hint">{bank.micr.length}/9 digits</span>
                    {errors[`bank_${idx}_micr`] && <span className="error-msg">⚠ {errors[`bank_${idx}_micr`]}</span>}
                  </div>

                  {/* Customer ID — required */}
                  <div className="field">
                    <label className="field-label">Customer ID <span className="required">*</span></label>
                    <input className={`input mono${errors[`bank_${idx}_customerId`] ? " error" : ""}`}
                      placeholder="Customer / CIF ID" value={bank.customerId}
                      onChange={(e) => setBankField(idx, "customerId", e.target.value)} />
                    {errors[`bank_${idx}_customerId`] && <span className="error-msg">⚠ {errors[`bank_${idx}_customerId`]}</span>}
                  </div>

                  {/* Net Banking ID — required */}
                  <div className="field">
                    <label className="field-label">Net Banking ID <span className="required">*</span></label>
                    <input className={`input${errors[`bank_${idx}_netBankingId`] ? " error" : ""}`}
                      placeholder="Internet banking login ID" value={bank.netBankingId}
                      onChange={(e) => setBankField(idx, "netBankingId", e.target.value)} />
                    {errors[`bank_${idx}_netBankingId`] && <span className="error-msg">⚠ {errors[`bank_${idx}_netBankingId`]}</span>}
                  </div>

                  {/* MO (Bank) — required */}
                  <div className="field">
                    <label className="field-label">Mobile (MO) <span className="required">*</span></label>
                    <input className={`input${errors[`bank_${idx}_mo`] ? " error" : ""}`}
                      type="tel" placeholder="Registered mobile" maxLength={10}
                      value={bank.mo} onChange={(e) => setBankField(idx, "mo", e.target.value.replace(/\D/g,""))} />
                    {errors[`bank_${idx}_mo`] && <span className="error-msg">⚠ {errors[`bank_${idx}_mo`]}</span>}
                  </div>

                  {/* Email — OPTIONAL (green in image) */}
                  <div className="field" style={{borderLeft:"3px solid #16a34a",paddingLeft:10}}>
                    <label className="field-label" style={{color:"#16a34a"}}>Email <span style={{fontSize:12,fontWeight:400}}>(Optional)</span></label>
                    <input className="input" type="email" placeholder="Registered email" value={bank.email}
                      onChange={(e) => setBankField(idx, "email", e.target.value)} />
                  </div>

                  {/* ATM Card No — OPTIONAL (green in image) */}
                  <div className="field" style={{borderLeft:"3px solid #16a34a",paddingLeft:10}}>
                    <label className="field-label" style={{color:"#16a34a"}}>ATM Card No <span style={{fontSize:12,fontWeight:400}}>(Optional)</span></label>
                    <input className="input mono" placeholder="16-digit card number" maxLength={16}
                      value={bank.atmCardNo}
                      onChange={(e) => setBankField(idx, "atmCardNo", e.target.value.replace(/\D/g,""))} />
                  </div>

                  {/* Expiry — OPTIONAL (green in image) */}
                  <div className="field" style={{borderLeft:"3px solid #16a34a",paddingLeft:10}}>
                    <label className="field-label" style={{color:"#16a34a"}}>Expiry <span style={{fontSize:12,fontWeight:400}}>(Optional)</span></label>
                    <input className="input mono" placeholder="MM/YY" maxLength={5} value={bank.expiry}
                      onChange={(e) => {
                        let v = e.target.value.replace(/[^\d/]/g,"");
                        if (v.length === 2 && !v.includes("/")) v += "/";
                        setBankField(idx, "expiry", v);
                      }} />
                  </div>

                  {/* CVV — OPTIONAL (green in image) */}
                  <div className="field" style={{borderLeft:"3px solid #16a34a",paddingLeft:10}}>
                    <label className="field-label" style={{color:"#16a34a"}}>CVV <span style={{fontSize:12,fontWeight:400}}>(Optional)</span></label>
                    <input className="input mono" placeholder="3-digit CVV" maxLength={3}
                      value={bank.cvv} onChange={(e) => setBankField(idx, "cvv", e.target.value.replace(/\D/g,""))} />
                  </div>

                  {/* Note (Bank) — OPTIONAL (green in image) */}
                  <div className="field" style={{ gridColumn: "1 / -1", borderLeft:"3px solid #16a34a", paddingLeft:10 }}>
                    <label className="field-label" style={{color:"#16a34a"}}>Note (Bank) <span style={{fontSize:12,fontWeight:400}}>(Optional)</span></label>
                    <textarea className="input" rows={2} placeholder="Any note for this bank account"
                      value={bank.note} onChange={(e) => setBankField(idx, "note", e.target.value)} />
                  </div>

                </div>
              </div>
            ))}

            <button className="btn btn-add" onClick={addBank}>+ Add Another Bank Account</button>
          </div>

          {/* ══════════════════════════════════════════════
              SECTION 4 — OTHER DETAILS (fully optional / green)
          ══════════════════════════════════════════════ */}
          <div className="form-section">
            <div className="section-header">
              <div className="section-icon">📝</div>
              <span className="section-title" style={{color:"var(--success,#16a34a)"}}>Other Details</span>
            </div>
            <div className="grid-3">
              <div className="field">
                <label className="field-label">Reference</label>
                <input className="input" placeholder="Reference" value={form.reference}
                  onChange={(e) => setField("reference", e.target.value)} />
              </div>
              <div className="field">
                <label className="field-label">Source for IPO</label>
                <input className="input" placeholder="IPO source / broker" value={form.sourceForIpo}
                  onChange={(e) => setField("sourceForIpo", e.target.value)} />
              </div>
              <div className="field">
                <label className="field-label">Note</label>
                <textarea className="input" rows={3} value={form.note}
                  onChange={(e) => setField("note", e.target.value)} />
              </div>
            </div>
          </div>

          {/* ── Submit & Sign Out ── */}
          <div className="submit-section">
            <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
              {loading ? "Saving…" : "Save Client Details"}
            </button>
            <button
              onClick={handleLogout}
              style={{marginTop:12,width:"100%",background:"transparent",border:"1.5px solid var(--border)",
                borderRadius:"var(--radius-md)",color:"var(--text-secondary)",padding:"11px 20px",
                fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit",transition:"all 0.15s"}}
              onMouseEnter={(e) => { e.target.style.borderColor="var(--danger)"; e.target.style.color="var(--danger)"; }}
              onMouseLeave={(e) => { e.target.style.borderColor="var(--border)"; e.target.style.color="var(--text-secondary)"; }}
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}